import type { Call } from '@entities/call';
import { CallTypes } from '@entities/call';
import type { BaseUsecase } from '@usecases/base-usecase';
import { RepositoryFactory } from '@main/factories/repository.factory';
import type { CallRepository } from '@infra/repositories/call.repository';
import type { UserRepository } from '@infra/repositories/user.repository';
//import { ControllerFactory } from '@main/factories/controller.factory';

export type ConnectedCallRequest = void;

export interface ConnectedCallRepository {
  fetchOutboundCall(): Promise<Call>;
}

export interface ConnectedCallEventPublisher {
  publish(
    call: Call,
    isRecordingPaused?: boolean,
    recordEnabled?: boolean,
  ): Promise<void>;
}

export interface ConnectedCallUsecase
  extends BaseUsecase<ConnectedCallRequest, void> {
  run(
    call: ConnectedCallRequest | ConnectedCallWithCrmOpsRequest,
  ): Promise<void>;
}

export class ConnectedCall implements ConnectedCallUsecase {
  private callRepository: CallRepository;
  private userRepository: UserRepository;

  constructor(
    private repository: ConnectedCallRepository,
    private publisher: ConnectedCallEventPublisher,
  ) {
    this.callRepository = RepositoryFactory.buildMemCallRepository();
    this.userRepository = RepositoryFactory.buildMemUserRepository();
  }

  async run(): Promise<void> {
    const call = await this.repository.fetchOutboundCall();

    let isRecordingPaused = true;
    let recordEnabled = true;

    const callDetails = await this.callRepository.fetchCallById(call.id);
    const userDetails = this.userRepository.getUserInfo();

    if (callDetails && userDetails?.numbers) {
      const matchingNumber = userDetails.numbers.find(
        (number) => number.normalized === callDetails.from,
      );

      if (matchingNumber) {
        isRecordingPaused = !matchingNumber.outbound_recording_activated;
        recordEnabled = matchingNumber.allow_outbound_recording_actions;
      }
    }
    if (call.type === CallTypes.OUTBOUND || call.type === CallTypes.INBOUND) {
      await this.publisher.publish(call, isRecordingPaused, recordEnabled);
    }
  }
}

export interface ConnectedCallCrmOps {
  preCall?: (callUuid: string, req: Call) => Promise<void>;
  postCall?: (req: Call) => Promise<void>;
}

export type ConnectedCallWithCrmOpsRequest = {
  callUuid: string;
  from: string;
  to: string;
};

export class ConnectedCallWithCrmOps extends ConnectedCall {
  constructor(
    private crmOps: ConnectedCallCrmOps,
    private callRepo: ConnectedCallRepository,
    publisher: ConnectedCallEventPublisher,
  ) {
    super(callRepo, publisher);
  }

  override async run(
    req: ConnectedCallWithCrmOpsRequest | void,
  ): Promise<void> {
    const call = await this.callRepo.fetchOutboundCall();

    if (req && this.crmOps.preCall) {
      await this.crmOps.preCall(req.callUuid, call);
    }

    await super.run();

    if (req && this.crmOps.postCall) {
      await this.crmOps.postCall(call);
    }

    //const prerecordedMessageController =
    //ControllerFactory.buildFetchPrerecordedMessagesController();

    //await prerecordedMessageController.handle();
  }
}
